// Keyboard shortcuts (board top bar «/» chip + reception speed keys):
//   /  focus the global search      N  new appointment
//   R  receipt voucher (سند قبض)    P  print the current document
// Bare keys only (no Ctrl/Alt/Meta), ignored while typing in a field. Arabic
// keyboard layouts are handled by `event.code` (physical key), so N/R/P work
// whatever the active layout.
//
// Handlers stack: the most recently registered handler for an action wins
// (a screen with an open receipt panel can take over 'print'), and unregistering
// restores the previous one.

import { useEffect } from 'react';

export type ShortcutAction = 'search' | 'new-appointment' | 'receipt' | 'print';

const CODE_TO_ACTION: Record<string, ShortcutAction> = {
  Slash: 'search',
  NumpadDivide: 'search',
  KeyN: 'new-appointment',
  KeyR: 'receipt',
  KeyP: 'print',
};

const KEY_TO_ACTION: Record<string, ShortcutAction> = { '/': 'search', n: 'new-appointment', r: 'receipt', p: 'print' };

type Handler = () => void;
const stacks = new Map<ShortcutAction, Handler[]>();

/** Register a handler; returns the unregister function. */
export function registerShortcut(action: ShortcutAction, handler: Handler): () => void {
  const stack = stacks.get(action) ?? [];
  stack.push(handler);
  stacks.set(action, stack);
  return () => {
    const s = stacks.get(action);
    if (!s) return;
    const i = s.lastIndexOf(handler);
    if (i >= 0) s.splice(i, 1);
  };
}

/** React hook form of registerShortcut. */
export function useShortcut(action: ShortcutAction, handler: Handler | null | undefined, enabled = true): void {
  useEffect(() => {
    if (!enabled || !handler) return;
    return registerShortcut(action, handler);
  }, [action, handler, enabled]);
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (t as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type);
  }
  return false;
}

/** Map a keyboard event to an action, or null. Exported for tests. */
export function actionForEvent(e: Pick<KeyboardEvent, 'code' | 'key' | 'ctrlKey' | 'altKey' | 'metaKey' | 'target'>): ShortcutAction | null {
  if (e.ctrlKey || e.altKey || e.metaKey) return null;
  if (isTypingTarget(e.target)) return null;
  return CODE_TO_ACTION[e.code] ?? KEY_TO_ACTION[e.key?.toLowerCase?.() ?? ''] ?? null;
}

/** Dispatch an action to its top handler. Returns true when handled. */
export function triggerShortcut(action: ShortcutAction): boolean {
  const s = stacks.get(action);
  const h = s?.[s.length - 1];
  if (!h) return false;
  h();
  return true;
}

/** Install the single global keydown listener; returns the uninstaller. */
export function installShortcuts(target: Window | Document = window): () => void {
  const onKey = (ev: Event) => {
    const e = ev as KeyboardEvent;
    if (e.defaultPrevented) return;
    const action = actionForEvent(e);
    if (!action) return;
    if (triggerShortcut(action)) e.preventDefault();
  };
  target.addEventListener('keydown', onKey);
  return () => target.removeEventListener('keydown', onKey);
}

/** Test hook. */
export function __resetShortcuts(): void {
  stacks.clear();
}
